import AlterCollectionCard from "@/components/Bundle/Collection/AlterCollectionCard";
import { useComments } from "@/hooks/useComment";
import { useDebounce } from "@/hooks/useCommon";
import { useLazyMetaInfos } from "@/hooks/useMeta";
import { useGetDisplayPreference } from "@/hooks/usePubmed";
import useTranslation from "@/hooks/useTranslation";
import { SearchContentListItem } from "@/interfaces/pubmed.interface";
import { showCollectorCommentsState } from "@/stores/collection";
import { currentUserState, immersiveLayzerState } from "@/stores/common";
import { pubmedState } from "@/stores/pubmed";
import { totalSanitize } from "@/utils/sanitize.util";
import { smoothScrollToTarget } from "@/utils/scroll.util";
import { List, Skeleton } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { Element } from "react-scroll";
import { useRecoilValue } from "recoil";
import Avatar from "./Avatar";
import AvatarWithCheck from "./AvatarWithCheck";
import Description from "./Description";
import ContentFooter from "./Footer";
import ContentHeader from "./Header";
import styles from "./styles.module.less";
import Tab from "./Tab";
import Title from "./Title";

const { footer, container, avatar } = styles;

export interface SearchContentProps {
  locationInfo?: { size: number; page: number };
  fetchResults: SearchContentListItem[];
  loadingFetch?: boolean;
  count: number;
  term?: string;
  loadingSearch?: boolean;
  pagination?: boolean;
  handleFetch?: ({
    page,
    size,
  }: {
    page?: number | undefined;
    size?: number | undefined;
  }) => void;
  Footer?: boolean | JSX.Element;
  Header?: boolean | JSX.Element;
  compact?: boolean;
  verbose?: boolean;
  showTotal?: boolean;
}

const Content: React.FC<SearchContentProps> = (props) => {
  const currentUser = useRecoilValue(currentUserState);
  useGetDisplayPreference();
  const {
    locationInfo,
    term,
    fetchResults,
    count,
    loadingSearch = false,
    loadingFetch = false,
    handleFetch,
    Footer,
    Header,
    pagination = true,
    compact = false,
    verbose: _verbose = true,
    showTotal = false,
  } = props;

  const { displayPreference } = useRecoilValue(pubmedState);

  const { t } = useTranslation("explore");

  const [iLoading, setILoading] = useState(false);

  const handleWithLoading = (func: Function) => {
    setILoading(true);
    smoothScrollToTarget("search-content")
      .then(() => func())
      .then(() => setILoading(false));
  };

  const handleChangePage = (page: number, size: number) => {
    handleWithLoading(() => handleFetch({ page, size }));
  };

  const loading = iLoading || loadingFetch;

  const shouldLoading = useMemo(() => {
    if (!loadingSearch && count > 0 && fetchResults?.length === 0) {
      if (count > 0 && fetchResults?.length === 0) {
        return false;
      }
      return true;
    }
    return loadingSearch;
  }, [fetchResults, loadingSearch, count]);

  const termToShow = useMemo(() => {
    return totalSanitize(
      term?.replace("trending[sb]", t("pubmed.content.trending")) ||
        t("pubmed.content.trending")
    );
  }, [term]);

  const verbose = useMemo(
    () =>
      _verbose &&
      currentUser?.id &&
      displayPreference?.verbose &&
      displayPreference?.defaultActiveKey === "Helper",
    [currentUser, displayPreference, _verbose]
  );

  const fatherIds = useMemo(() => fetchResults?.map((e) => e.ePmid), [
    fetchResults,
  ]);

  const { getMeta } = useLazyMetaInfos(fatherIds);

  useEffect(() => {
    if (displayPreference?.verbose) {
      getMeta();
    }
  }, [displayPreference, fatherIds]);

  const showCollectorComments = useRecoilValue(showCollectorCommentsState);

  useComments(
    fatherIds,
    displayPreference.defaultActiveKey !== "AllComment" ||
      fetchResults?.length === 0,
    "all"
  );

  useComments(
    fatherIds,
    !currentUser.id ||
      displayPreference.defaultActiveKey !== "MyComment" ||
      fetchResults?.length === 0,
    "user"
  );

  useComments(
    fatherIds,
    !showCollectorComments.show ||
      displayPreference.defaultActiveKey !== "CollectorComment" ||
      fetchResults?.length === 0,
    "collector"
  );

  const hovering = useRecoilValue(immersiveLayzerState);

  const debouncedHovering = useDebounce(hovering, 2000);

  return (
    <Element name="search-content">
      <List
        dataSource={fetchResults ? fetchResults : []}
        className={container}
        style={compact ? { padding: 0 } : undefined}
        itemLayout="vertical"
        size="large"
        loading={{ spinning: shouldLoading, delay: 200 }}
        pagination={
          pagination
            ? {
                onChange: handleChangePage,
                pageSize: locationInfo.size,
                total: count,
                current: locationInfo.page,
                showQuickJumper: true,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
                showTotal: showTotal
                  ? (total, range) => {
                      return (
                        <div className={styles.total}>
                          <span>
                            <span className={styles.number}>{range[0]} - </span>
                            <span className={styles.number}>
                              {range[1]}
                            </span> of{" "}
                            <span className={styles.number}>{total}</span> items
                          </span>
                        </div>
                      );
                    }
                  : undefined,
              }
            : undefined
        }
        footer={Footer === false ? undefined : Footer ?? <ContentFooter />}
        header={
          Header === false
            ? undefined
            : Header ?? (
                <ContentHeader
                  term={termToShow}
                  pmids={fetchResults?.map((e) => e?.ePmid)}
                  verbose={verbose}
                />
              )
        }
        renderItem={(item, _index) => {
          const {
            ePmid,
            publicationTypes,
            articleTitle,
            authors,
            journalInfo,
            eDoi,
            articleDate,
            articleIds,
            journal,
            medlineJournalInfo,
          } = item;

          const index = (locationInfo?.page - 1) * locationInfo?.size + _index;

          return (
            <List.Item key={ePmid}>
              <Skeleton loading={loading} active avatar paragraph={{ rows: 4 }}>
                <List.Item.Meta
                  avatar={
                    <AvatarWithCheck verbose={verbose} id={ePmid}>
                      <Avatar className={avatar} arr={publicationTypes} />
                    </AvatarWithCheck>
                  }
                  title={
                    <Title
                      ePmid={ePmid}
                      articleTitle={articleTitle}
                      term={term}
                      verbose={verbose}
                      index={index}
                    />
                  }
                  description={
                    <Description
                      description={{
                        authors,
                        journalInfo,
                        publicationTypes,
                        eDoi,
                        ePmid,
                        articleDate,
                        articleIds,
                        journal,
                        medlineJournalInfo,
                      }}
                    />
                  }
                />
                <Tab fetchResultsEach={item} />
              </Skeleton>
            </List.Item>
          );
        }}
      />
      {verbose && <AlterCollectionCard />}
      {displayPreference?.verbose && displayPreference?.immersiveMode && (
        <div
          className={styles.overlay}
          style={{
            opacity: debouncedHovering ? 0.6 : 0,
            zIndex: debouncedHovering ? 700 : -100,
          }}
        />
      )}
    </Element>
  );
};

export default Content;
